import { useEffect, useState } from "react";
import { Save, Type, BookOpen, ArrowUp, ArrowDown, LayoutList, Eye, EyeOff, Navigation, RotateCcw, Plus, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { UI_TEXTS_DEFAULTS, invalidateUiTexts } from "@/lib/uiTexts";

const SECTION_LABELS = {
  marquee: "Bandeau roulant",
  results: "Résultats & matchs",
  poll: "Sondage communauté",
  manifesto: "Manifeste",
  fanwall: "Mur de fans",
  news: "Actualités",
  newsletter: "Newsletter",
  partners: "Bandeau partenaires",
  banner: "Bannière boutique",
};

const UITEXT_GROUPS = [
  { title: "Onglets de navigation", fields: [
    ["nav_home", "Onglet Accueil"], ["nav_shop", "Onglet Boutique"], ["nav_rosters", "Onglet Rosters"],
    ["nav_drafter", "Onglet Drafter"], ["nav_results", "Onglet Résultats"], ["nav_news", "Onglet Actualités"], ["nav_partners", "Onglet Partenaires"], ["nav_contact", "Onglet Contact"],
  ]},
  { title: "Cartes de résultats / matchs", fields: [
    ["match_opponent_link", "Lien adverse ({opponent} = nom adverse)"], ["match_stream_link", "Lien stream"],
    ["match_mvp_label", "Libellé MVP"], ["match_countdown_prefix", "Texte du compte à rebours"],
  ]},
  { title: "Page Boutique", fields: [
    ["shop_label", "Petit label orange"], ["shop_title", "Grand titre"], ["shop_teaser_soon", "Texte des produits à venir"],
  ]},
  { title: "Page Résultats", fields: [
    ["results_label", "Petit label orange"], ["results_title", "Grand titre"],
    ["results_filter_all", "Filtre « Tous »"], ["results_empty", "Message si aucun match"],
  ]},
  { title: "Page Rosters", fields: [
    ["rosters_label", "Petit label orange"], ["rosters_title", "Grand titre"],
    ["roster_opgg_label", "Lien stats LoL/TFT"], ["roster_r6_label", "Lien stats R6"],
  ]},
  { title: "Page Actualités", fields: [
    ["news_page_label", "Petit label orange"], ["news_page_title", "Grand titre"],
  ]},
  { title: "Page Partenaires", fields: [
    ["partners_label", "Petit label orange"], ["partners_title", "Grand titre"], ["partners_subtitle", "Description sous le titre"],
  ]},
  { title: "Page Contact", fields: [
    ["contact_label", "Petit label orange"], ["contact_title", "Grand titre"], ["contact_socials_label", "Titre du bloc réseaux"],
  ]},
  { title: "Page FAQ", fields: [
    ["faq_label", "Petit label orange"], ["faq_title", "Grand titre"],
  ]},
  { title: "Accueil — sections", fields: [
    ["home_competition_label", "Label au-dessus des matchs"], ["home_community_label", "Label au-dessus des actus"],
    ["home_manifesto_title", "Titre du manifeste"],
  ]},
  { title: "Mur de fans", fields: [
    ["fanwall_label", "Petit label orange"], ["fanwall_title", "Grand titre"], ["fanwall_subtitle", "Description"],
    ["fanwall_submit", "Bouton d'envoi"], ["fanwall_empty", "Message si mur vide"],
  ]},
  { title: "Pied de page", fields: [
    ["footer_baseline", "Phrase sous le logo"],
  ]},
];

const FIELDS = [
  { key: "badge", label: "Badge au-dessus du titre" },
  { key: "title_1", label: "Titre — ligne 1" },
  { key: "title_2", label: "Titre — ligne 2 (en orange)" },
  { key: "intro", label: "Texte d'introduction", textarea: true },
  { key: "cta_primary", label: "Bouton principal (boutique)" },
  { key: "cta_secondary", label: "Bouton secondaire (équipe)" },
  { key: "cta_discord", label: "Bouton Discord (communauté) — visible si le lien Discord est renseigné dans Réseaux & Contact" },
  { key: "matches_title", label: "Titre section Résultats" },
  { key: "poll_title", label: "Titre section Sondage" },
  { key: "news_title", label: "Titre section Actualités" },
  { key: "banner_title", label: "Bannière boutique — titre" },
  { key: "banner_text", label: "Bannière boutique — texte", textarea: true },
  { key: "banner_cta", label: "Bannière boutique — bouton" },
];

const inputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

export default function HomeTextsTab() {
  const [texts, setTexts] = useState({});
  const [marquee, setMarquee] = useState({ text: "", color: "#FF5E00" });
  const [chapters, setChapters] = useState([]);
  const [layout, setLayout] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [uiTexts, setUiTexts] = useState(UI_TEXTS_DEFAULTS);
  const [navOrder, setNavOrder] = useState([]);
  const [navCustom, setNavCustom] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/home-texts"), api.get("/marquee"), api.get("/chapters"), api.get("/home-layout"), api.get("/ui-texts"), api.get("/nav")])
      .then(([t, m, c, l, u, n]) => { setTexts(t.data); setMarquee(m.data); setChapters(c.data.items || []); setLayout(l.data.order || []); setHidden(l.data.hidden || []); setUiTexts({ ...UI_TEXTS_DEFAULTS, ...u.data }); setNavOrder(n.data.order || []); setNavCustom(n.data.custom || []); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await Promise.all([
        api.put("/admin/home-texts", texts),
        api.put("/admin/marquee", marquee),
        api.put("/admin/chapters", { items: chapters }),
        api.put("/admin/home-layout", { order: layout, hidden }),
        api.put("/admin/ui-texts", { texts: uiTexts }),
        api.put("/admin/nav", { order: navOrder, custom: navCustom }),
      ]);
      invalidateUiTexts();
      toast.success("Page d'accueil mise à jour");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const moveNav = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= navOrder.length) return;
    const next = [...navOrder];
    [next[i], next[j]] = [next[j], next[i]];
    setNavOrder(next);
  };

  const moveSection = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= layout.length) return;
    const next = [...layout];
    [next[i], next[j]] = [next[j], next[i]];
    setLayout(next);
  };

  const toggleHidden = (key) =>
    setHidden((h) => (h.includes(key) ? h.filter((k) => k !== key) : [...h, key]));

  const resetTexts = async () => {
    if (!window.confirm("Restaurer tous les textes du site à leurs valeurs d'origine ?")) return;
    try {
      await api.put("/admin/ui-texts", { texts: UI_TEXTS_DEFAULTS });
      setUiTexts(UI_TEXTS_DEFAULTS);
      invalidateUiTexts();
      toast.success("Textes restaurés aux valeurs d'origine");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const setChapter = (i, patch) => setChapters(chapters.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  if (loading) return <p className="text-slate-500">Chargement...</p>;

  return (
    <form onSubmit={save} className="card-tactical grid max-w-2xl gap-4 p-6" data-testid="admin-home-texts-form">
      <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
        <Type className="h-5 w-5 text-orange-500" /> Textes de la page d'accueil
      </p>
      <p className="text-xs text-slate-500">Modifiez n'importe quel texte de la page d'accueil. Un champ vide garde le texte par défaut.</p>
      {FIELDS.map((f) => (
        <label key={f.key} className="text-xs font-bold uppercase tracking-widest text-slate-400">
          {f.label}
          {f.textarea ? (
            <textarea value={texts[f.key] || ""} rows={2} data-testid={`admin-text-${f.key}`}
              onChange={(e) => setTexts({ ...texts, [f.key]: e.target.value })} className={inputCls + " mt-1"} />
          ) : (
            <input value={texts[f.key] || ""} data-testid={`admin-text-${f.key}`}
              onChange={(e) => setTexts({ ...texts, [f.key]: e.target.value })} className={inputCls + " mt-1"} />
          )}
        </label>
      ))}

      <div className="mt-2 border-t border-orange-500/10 pt-4">
        <p className="font-heading text-lg font-bold uppercase">Bandeau roulant</p>
        <p className="mt-1 text-xs text-slate-500">Le bandeau défilant sous le héros. Séparez les mots/phrases par « /// ».</p>
        <label className="mt-3 block text-xs font-bold uppercase tracking-widest text-slate-400">
          Texte du bandeau
          <textarea value={marquee.text} rows={2} data-testid="admin-marquee-text"
            placeholder="Emisphere Esport /// League of Legends /// ..."
            onChange={(e) => setMarquee({ ...marquee, text: e.target.value })} className={inputCls + " mt-1"} />
        </label>
        <label className="mt-3 flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-slate-400">
          Couleur du texte
          <input type="color" value={marquee.color} data-testid="admin-marquee-color"
            onChange={(e) => setMarquee({ ...marquee, color: e.target.value })}
            className="h-9 w-16 cursor-pointer rounded-lg border border-input bg-[#060B18] p-1" />
          <span className="font-mono2 text-sm" style={{ color: marquee.color }}>{marquee.color}</span>
        </label>
      </div>

      <div className="mt-2 border-t border-orange-500/10 pt-4">
        <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
          <BookOpen className="h-5 w-5 text-orange-500" /> Section Manifeste (3 chapitres)
        </p>
        <p className="mt-1 text-xs text-slate-500">Les 3 blocs numérotés de la page d'accueil. Le lien doit être une page du site (ex : /boutique, /resultats, /actualites, /rosters, /contact).</p>
        {chapters.map((c, i) => (
          <div key={i} className="mt-4 grid gap-3 rounded-lg border border-orange-500/10 bg-[#060B18] p-4" data-testid={`admin-chapter-${i}`}>
            <div className="grid grid-cols-[70px_1fr] gap-3">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
                Numéro
                <input value={c.num} data-testid={`admin-chapter-num-${i}`}
                  onChange={(e) => setChapter(i, { num: e.target.value })} className={inputCls + " mt-1"} />
              </label>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
                Titre
                <input value={c.title} data-testid={`admin-chapter-title-${i}`}
                  onChange={(e) => setChapter(i, { title: e.target.value })} className={inputCls + " mt-1"} />
              </label>
            </div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
              Texte
              <textarea value={c.text} rows={3} data-testid={`admin-chapter-text-${i}`}
                onChange={(e) => setChapter(i, { text: e.target.value })} className={inputCls + " mt-1"} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
                Libellé du lien
                <input value={c.linkLabel} data-testid={`admin-chapter-linklabel-${i}`}
                  onChange={(e) => setChapter(i, { linkLabel: e.target.value })} className={inputCls + " mt-1"} />
              </label>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
                Page du lien
                <input value={c.link} placeholder="/boutique" data-testid={`admin-chapter-link-${i}`}
                  onChange={(e) => setChapter(i, { link: e.target.value })} className={inputCls + " mt-1"} />
              </label>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-2 border-t border-orange-500/10 pt-4">
        <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
          <LayoutList className="h-5 w-5 text-orange-500" /> Ordre des sections
        </p>
        <p className="mt-1 text-xs text-slate-500">Choisissez la place de chaque section sur la page d'accueil (le héros reste toujours en premier). L'icône œil permet de masquer temporairement une section sans la supprimer.</p>
        <div className="mt-3 grid gap-2" data-testid="admin-layout-list">
          {layout.map((key, i) => {
            const isHidden = hidden.includes(key);
            return (
            <div key={key} data-testid={`admin-layout-row-${key}`}
              className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-2.5 ${isHidden ? "border-slate-700/60 bg-[#060B18]/50 opacity-60" : "border-orange-500/10 bg-[#060B18]"}`}>
              <span className="text-sm font-bold uppercase tracking-wide text-slate-200">
                <span className="mr-2 font-mono2 text-orange-500">{String(i + 1).padStart(2, "0")}</span>
                {SECTION_LABELS[key] || key}
                {isHidden && <span className="ml-2 rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-300">Masquée</span>}
              </span>
              <div className="flex gap-1">
                <button type="button" onClick={() => toggleHidden(key)}
                  data-testid={`admin-layout-toggle-${key}`} aria-label={`${isHidden ? "Afficher" : "Masquer"} ${SECTION_LABELS[key]}`}
                  className={`rounded-md border p-1.5 transition-colors ${isHidden ? "border-slate-600 text-slate-400 hover:border-emerald-500 hover:text-emerald-400" : "border-slate-600 text-slate-300 hover:border-orange-500 hover:text-orange-400"}`}>
                  {isHidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button type="button" onClick={() => moveSection(i, -1)} disabled={i === 0}
                  data-testid={`admin-layout-up-${key}`} aria-label={`Monter ${SECTION_LABELS[key]}`}
                  className="rounded-md border border-slate-600 p-1.5 text-slate-300 transition-colors hover:border-orange-500 hover:text-orange-400 disabled:opacity-30">
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => moveSection(i, 1)} disabled={i === layout.length - 1}
                  data-testid={`admin-layout-down-${key}`} aria-label={`Descendre ${SECTION_LABELS[key]}`}
                  className="rounded-md border border-slate-600 p-1.5 text-slate-300 transition-colors hover:border-orange-500 hover:text-orange-400 disabled:opacity-30">
                  <ArrowDown className="h-4 w-4" />
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      <div className="mt-2 border-t border-orange-500/10 pt-4" data-testid="admin-ui-texts-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
            <Navigation className="h-5 w-5 text-orange-500" /> Textes du site (toutes les pages)
          </p>
          <button type="button" onClick={resetTexts} data-testid="admin-uitexts-reset"
            className="flex items-center gap-2 rounded-lg border border-slate-600 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-300 transition-colors hover:border-orange-500 hover:text-orange-400">
            <RotateCcw className="h-3.5 w-3.5" /> Restaurer les textes d'origine
          </button>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Renommez les onglets du menu, les titres de chaque page et les libellés affichés sur le site.
          Astuce : utilisez {"{opponent}"} dans le lien adverse pour insérer le nom de l'équipe adverse.
        </p>
        {navOrder.length > 0 && (
          <div className="mt-5" data-testid="admin-nav-order-section">
            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-orange-400">Ordre des onglets du menu</p>
            <div className="grid max-w-2xl gap-2">
              {navOrder.map((key, i) => {
                const custom = navCustom.find((c) => c.id === key);
                return (
                  <div key={key} data-testid={`admin-nav-order-row-${key}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-orange-500/10 bg-[#060B18] px-4 py-2">
                    {custom ? (
                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                        <input value={custom.label} placeholder="Nom de l'onglet" data-testid={`admin-nav-custom-label-${key}`}
                          onChange={(e) => setNavCustom(navCustom.map((c) => (c.id === key ? { ...c, label: e.target.value } : c)))}
                          className={inputCls + " !w-40"} />
                        <input value={custom.url} placeholder="/ma-page ou https://..." data-testid={`admin-nav-custom-url-${key}`}
                          onChange={(e) => setNavCustom(navCustom.map((c) => (c.id === key ? { ...c, url: e.target.value } : c)))}
                          className={inputCls + " !w-56"} />
                      </div>
                    ) : (
                      <span className="text-sm font-bold uppercase tracking-wide text-slate-200">
                        <span className="mr-2 font-mono2 text-orange-500">{String(i + 1).padStart(2, "0")}</span>
                        {uiTexts[`nav_${key}`]}
                      </span>
                    )}
                    <div className="flex gap-1">
                      <button type="button" onClick={() => moveNav(i, -1)} disabled={i === 0}
                        data-testid={`admin-nav-order-up-${key}`} aria-label={`Monter ${key}`}
                        className="rounded-md border border-slate-600 p-1 text-slate-300 transition-colors hover:border-orange-500 hover:text-orange-400 disabled:opacity-30">
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => moveNav(i, 1)} disabled={i === navOrder.length - 1}
                        data-testid={`admin-nav-order-down-${key}`} aria-label={`Descendre ${key}`}
                        className="rounded-md border border-slate-600 p-1 text-slate-300 transition-colors hover:border-orange-500 hover:text-orange-400 disabled:opacity-30">
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                      {custom && (
                        <button type="button" data-testid={`admin-nav-custom-delete-${key}`} aria-label={`Supprimer ${custom.label}`}
                          onClick={() => { setNavCustom(navCustom.filter((c) => c.id !== key)); setNavOrder(navOrder.filter((k) => k !== key)); }}
                          className="rounded-md border border-red-500/40 p-1 text-red-400 transition-colors hover:bg-red-500/10">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <button type="button" data-testid="admin-nav-custom-add"
              onClick={() => {
                const id = `custom_${Date.now()}`;
                setNavCustom([...navCustom, { id, label: "", url: "" }]);
                setNavOrder([...navOrder, id]);
              }}
              className="mt-3 flex items-center gap-2 rounded-lg border border-orange-500/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-orange-400 transition-all hover:bg-orange-500/10">
              <Plus className="h-4 w-4" /> Ajouter un onglet
            </button>
          </div>
        )}
        {UITEXT_GROUPS.map((g) => (
          <div key={g.title} className="mt-5">
            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-orange-400">{g.title}</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {g.fields.map(([k, lbl]) => (
                <label key={k} className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  {lbl}
                  <input value={uiTexts[k] ?? ""} data-testid={`admin-uitext-${k}`}
                    onChange={(e) => setUiTexts({ ...uiTexts, [k]: e.target.value })} className={inputCls + " mt-1"} />
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button type="submit" data-testid="admin-home-texts-save"
        className="flex w-fit items-center gap-2 rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
        <Save className="h-4 w-4" /> Enregistrer
      </button>
    </form>
  );
}


